import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { HiHeart, HiOutlineHeart, HiEllipsisVertical, HiCheck, HiXMark, HiArrowsRightLeft, HiTrash, HiMiniPencilSquare } from 'react-icons/hi2';

import TableDateFormatter from '../../../../../../../../components/common/Table/TableDateFormatter';
import { ProductStock } from '../../../../types';
import LossConfirm from './components/LossConfirm/LossConfirm';
import './Displayer.scss';



type EditingInfoType = { id: number; storageId: number; field: 'reported' | 'min' } | null;

// Props for the main Displayer component
interface DisplayerProps {
    productStockData: ProductStock[];
    filteredStockList: ProductStock[];
    editingInfo: EditingInfoType;
    tempReportedStock: string;
    tempMinStock: string;
    selectedAcronyms: any[];
    setSelectedAcronyms: (selected: any[]) => void;
    selectedStorages: any[];
    setSelectedStorages: (selected: any[]) => void;
    setEditingInfo: (info: EditingInfoType) => void;
    setShowCreateStockModal: any;
    setTempReportedStock: (value: string) => void;
    setTempMinStock: (value: string) => void;
    onUpdateReportedStock: (id: number, product_id: string, storageId: number, newValue: number) => void;
    onUpdateMinStock: (item: ProductStock, newValue: number) => void;
    onViewMovements: (stockItem: ProductStock) => void;
    onEqualize: (stockItem: ProductStock) => void;
    onTransfer: (stockItem: ProductStock) => void;
    onDelete: (stockItem: ProductStock) => void;
    onToggleFavorite: (item: ProductStock) => void;
    onToggleFollowing: (item: ProductStock) => void;
}

// Helpers
const getStockStatus = (stock: ProductStock): { className: string; label: string; badge: string } | null => {
    if (stock.min_quantity === null || stock.min_quantity === undefined) return null;
    if (stock.reported_stock < stock.min_quantity) return { className: 'status-red', label: 'Bajo', badge: 'mp-badge-danger' };
    if (stock.reported_stock <= stock.min_quantity * 1.05) return { className: 'status-yellow', label: 'Óptimo', badge: 'mp-badge-amber' };
    return { className: 'status-green', label: 'Excedente', badge: 'mp-badge-success' };
};

// Table row for a single stock item
const StockTableRow: React.FC<{
    item: ProductStock;
    editingInfo: EditingInfoType;
    tempReportedStock: string;
    setTempReportedStock: (val: string) => void;
    tempMinStock: string;
    setTempMinStock: (val: string) => void;
    onSaveReported: () => void;
    onSaveMin: () => void;
    onCancel: () => void;
    onEditClick: (field: 'reported' | 'min') => void;
    onViewMovements: (item: ProductStock) => void;
    onEqualize: (item: ProductStock) => void;
    onTransfer: (item: ProductStock) => void;
    onDelete: (item: ProductStock) => void;
    onToggleFavorite: (item: ProductStock) => void;
    onToggleFollowing: (item: ProductStock) => void;
}> = ({ item, editingInfo, tempReportedStock, setTempReportedStock, tempMinStock, setTempMinStock, onSaveReported, onSaveMin, onCancel, onEditClick, onToggleFavorite, ...handlers }) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const isEditing = editingInfo?.id === item.id && editingInfo?.storageId === item.storage_unit_id;
    const editingField = isEditing ? editingInfo.field : null;
    const status = getStockStatus(item);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
        };
        if (showMenu) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMenu]);

    const renderNumberInput = (field: 'reported' | 'min', value: string, setValue: (v: string) => void, onSave: () => void) => (
        <div className="stock-edit-inline">
            <input
                type="number"
                min="0"
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || (!v.includes('-') && !isNaN(Number(v)))) setValue(v);
                }}
                autoFocus
                className="stock-edit-input"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave();
                    else if (e.key === 'Escape') onCancel();
                    else if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                }}
            />
            <button onClick={onSave} className="stock-edit-btn confirm"><HiCheck size={16} /></button>
            <button onClick={onCancel} className="stock-edit-btn cancel"><HiXMark size={16} /></button>
        </div>
    );

    return (
        <tr>
            {/* Producto */}
            <td>
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className="font-semibold truncate cursor-pointer transition-colors hover:text-[#FC7556]"
                        style={{ color: 'var(--strong-color)' }}
                        onClick={() => handlers.onViewMovements(item)}
                        title="Ver movimientos"
                    >
                        {item.name}
                    </span>
                    <button
                        className="stock-fav-btn flex-shrink-0"
                        onClick={() => onToggleFavorite(item)}
                        title={(item as any).is_favorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                    >
                        {(item as any).is_favorite ? <HiHeart className="text-[#FC7556]" size={14} /> : <HiOutlineHeart size={14} />}
                    </button>
                </div>
            </td>

            {/* Sistema */}
            <td className="text-center tabular-nums">
                <span className="font-bold">{item.system_stock}</span>
                <span className="text-xs opacity-40 ml-1">{item.acronym}</span>
            </td>

            {/* Real — editable */}
            <td className="text-center tabular-nums" style={{ whiteSpace: 'nowrap' }}>
                {isEditing && editingField === 'reported' ? (
                    renderNumberInput('reported', tempReportedStock, setTempReportedStock, onSaveReported)
                ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span
                            className="font-bold cursor-pointer stock-editable-value"
                            onClick={() => onEditClick('reported')}
                            title="Click para editar"
                        >
                            {item.reported_stock}
                            <HiMiniPencilSquare className="stock-editable-icon" />
                        </span>
                        <span className="text-xs opacity-40">{item.acronym}</span>
                    </span>
                )}
            </td>

            {/* Mínimo — editable */}
            <td className="text-center tabular-nums" style={{ whiteSpace: 'nowrap' }}>
                {isEditing && editingField === 'min' ? (
                    renderNumberInput('min', tempMinStock, setTempMinStock, onSaveMin)
                ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span
                            className="font-bold cursor-pointer stock-editable-value"
                            onClick={() => onEditClick('min')}
                            title="Click para editar"
                        >
                            {item.min_quantity ?? 'N/A'}
                            <HiMiniPencilSquare className="stock-editable-icon" />
                        </span>
                        <span className="text-xs opacity-40">{item.acronym}</span>
                    </span>
                )}
            </td>

            {/* Almacén */}
            <td>
                <span className="mp-badge mp-badge-neutral text-[11px]">{item.storage_unit_name}</span>
            </td>

            {/* Estado */}
            <td className="text-center">
                {status && <span className={`mp-badge ${status.badge}`}>{status.label}</span>}
            </td>

            {/* Actualizado */}
            <td className="opacity-50 text-xs">
                {item.last_stock_update && (
                    <TableDateFormatter data={String(item.last_stock_update)} stopCounting={true} isTimestamp={true} isRealTime={false} />
                )}
            </td>

            {/* Acciones */}
            <td className="text-center">
                <div className="relative" ref={menuRef}>
                    <button className="mp-btn-icon" onClick={() => setShowMenu(!showMenu)} title="Acciones">
                        <HiEllipsisVertical size={16} />
                    </button>
                    {showMenu && (
                        <div className="stock-actions-dropdown">
                            <div className="stock-action-item" onClick={() => { handlers.onTransfer(item); setShowMenu(false); }}>
                                <HiArrowsRightLeft size={14} /> Transferir
                            </div>
                            <div className="stock-action-item danger" onClick={() => { handlers.onDelete(item); setShowMenu(false); }}>
                                <HiTrash size={14} /> Eliminar
                            </div>
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
};

// Mobile card for a single stock item
const StockMobileCard: React.FC<{
    item: ProductStock;
    onViewMovements: (item: ProductStock) => void;
    onToggleFavorite: (item: ProductStock) => void;
}> = ({ item, onViewMovements, onToggleFavorite }) => {
    const status = getStockStatus(item);
    const accentClass = status?.className === 'status-red' ? 'mp-mobile-card-accent-danger'
        : status?.className === 'status-yellow' ? 'mp-mobile-card-accent-warning'
        : status?.className === 'status-green' ? 'mp-mobile-card-accent-success'
        : '';

    return (
        <div className={`mp-mobile-card ${accentClass}`} onClick={() => onViewMovements(item)}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>
                        {item.name}
                    </span>
                    <button
                        className="stock-fav-btn flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(item); }}
                    >
                        {(item as any).is_favorite ? <HiHeart className="text-[#FC7556]" size={13} /> : <HiOutlineHeart size={13} />}
                    </button>
                </div>
                {status && <span className={`mp-badge ${status.badge} text-[10px]`}>{status.label}</span>}
            </div>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="mp-badge mp-badge-neutral text-[10px]">{item.storage_unit_name}</span>
                    <span className="text-xs opacity-40" style={{ color: 'var(--text-color)' }}>
                        S:{item.system_stock} · R:{item.reported_stock} · M:{item.min_quantity ?? '-'}
                    </span>
                </div>
                {item.last_stock_update && (
                    <span className="text-[10px] opacity-35 flex-shrink-0" style={{ color: 'var(--text-color)' }}>
                        <TableDateFormatter data={String(item.last_stock_update)} stopCounting={true} isTimestamp={true} isRealTime={false} />
                    </span>
                )}
            </div>
        </div>
    );
};


// Main Displayer component
const Displayer: React.FC<DisplayerProps> = (props) => {
  const { editingInfo, setEditingInfo, tempReportedStock, setTempReportedStock, onUpdateReportedStock, tempMinStock, setTempMinStock, onUpdateMinStock } = props;

  const [showLossConfirm, setShowLossConfirm] = React.useState(false);
  const [pendingUpdate, setPendingUpdate] = React.useState<{ item: ProductStock; newValue: number } | null>(null);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sort the filtered stock list with favorites first, then by recentness
  const sortedStockList = React.useMemo(() => {
    return [...props.filteredStockList].sort((a, b) => {
      const aIsFavorite = (a as any).is_favorite || false;
      const bIsFavorite = (b as any).is_favorite || false;
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      const aDate = new Date(a.last_stock_update || 0).getTime();
      const bDate = new Date(b.last_stock_update || 0).getTime();
      return bDate - aDate;
    });
  }, [props.filteredStockList]);

  const handleToggleFavorite = async (item: ProductStock) => { props.onToggleFavorite(item); };
  const handleToggleFollowing = async (item: ProductStock) => { props.onToggleFollowing(item); };

  const handleSaveReported = (item: ProductStock) => {
    if (tempReportedStock === '') { toast.error('El campo no puede estar vacío.'); return; }
    const newValue = Number(tempReportedStock);
    if (isNaN(newValue)) { toast.error('Valor inválido.'); return; }
    if (newValue < item.system_stock) {
      setPendingUpdate({ item, newValue });
      setShowLossConfirm(true);
      return;
    }
    onUpdateReportedStock(item.id, item.product_id, item.storage_unit_id, newValue);
    setEditingInfo(null);
  };

  const confirmLossAndUpdate = () => {
    if (!pendingUpdate) return;
    const { item, newValue } = pendingUpdate;
    onUpdateReportedStock(item.id, item.product_id, item.storage_unit_id, newValue);
    setShowLossConfirm(false);
    setPendingUpdate(null);
    setEditingInfo(null);
  };

  const handleSaveMin = (item: ProductStock) => {
    if (tempMinStock === '') { toast.error('El campo no puede estar vacío.'); return; }
    onUpdateMinStock(item, Number(tempMinStock));
    setEditingInfo(null);
  };

  return (
    <>
      {isMobile ? (
        /* Mobile: card-based layout */
        <div className="flex flex-col gap-2.5 p-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {sortedStockList.map((item) => (
            <StockMobileCard
              key={`${item.id}-${item.storage_unit_id}`}
              item={item}
              onViewMovements={props.onViewMovements}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
        </div>
      ) : (
        /* Desktop: proper mp-table */
        <div className="mp-table-card">
          <div className="overflow-x-auto overflow-y-auto mp-scrollbar" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <table className="mp-table">
              <thead className="mp-table-head">
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Sistema</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Real</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Mínimo</th>
                  <th style={{ width: 130 }}>Almacén</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Estado</th>
                  <th style={{ width: 150 }}>Actualizado</th>
                  <th style={{ width: 50, textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody className="mp-table-body">
                {sortedStockList.map((item) => (
                  <StockTableRow
                    key={`${item.id}-${item.storage_unit_id}`}
                    item={item}
                    editingInfo={editingInfo}
                    tempReportedStock={tempReportedStock}
                    setTempReportedStock={setTempReportedStock}
                    tempMinStock={tempMinStock}
                    setTempMinStock={setTempMinStock}
                    onEditClick={(field) => {
                      setEditingInfo({ id: item.id, storageId: item.storage_unit_id, field });
                      if (field === 'reported') {
                        setTempReportedStock(item.reported_stock.toString());
                      } else {
                        setTempMinStock(item.min_quantity?.toString() ?? '0');
                      }
                    }}
                    onSaveReported={() => handleSaveReported(item)}
                    onSaveMin={() => handleSaveMin(item)}
                    onCancel={() => setEditingInfo(null)}
                    onViewMovements={props.onViewMovements}
                    onEqualize={props.onEqualize}
                    onTransfer={props.onTransfer}
                    onDelete={props.onDelete}
                    onToggleFavorite={handleToggleFavorite}
                    onToggleFollowing={handleToggleFollowing}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mp-table-footer">
            <span className="text-xs opacity-35">{sortedStockList.length} productos</span>
          </div>
        </div>
      )}

      {showLossConfirm && pendingUpdate && (
        <LossConfirm
          show={showLossConfirm}
          onClose={() => { setShowLossConfirm(false); setPendingUpdate(null); }}
          onConfirm={confirmLossAndUpdate}
          systemStock={pendingUpdate.item.system_stock}
          newRealStock={pendingUpdate.newValue}
          acronym={pendingUpdate.item.acronym}
        />
      )}
    </>
  );
};

export default Displayer;